// controllers/useEducationController.js
import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useSession } from '../context/SessionContext';

export default function useEducationController() {
  const { session } = useSession();
  const [educations, setEducations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch educations from the database
  const fetchEducations = async () => {
    try {
      // console.log('session...', session);
      setLoading(true);
      const { data: educations, error: educationsError } = await supabase
        .from('education')
        .select('*')
        .eq('user_id', session.user.id);

      if (educationsError) throw educationsError;

      // Ambil gambar untuk setiap education
      const educationsWithImages = await Promise.all(
        educations.map(async (edu) => {
          const { data: images, error: imagesError } = await supabase
            .from('image')
            .select('path')
            .eq('category_id', edu.id)
            .eq('category', 'education') // Filter by category

          if (imagesError) throw imagesError;

          // Generate public URL untuk setiap gambar
          const imagesWithPublicUrl = images.map((image) => ({
            ...image,
            publicUrl: supabase.storage.from('document').getPublicUrl(image.path).data.publicUrl,
          }));

          return { ...edu, images: imagesWithPublicUrl };
        })
      );

      setEducations(educationsWithImages);
    } catch (error) {
      console.error('Error fetching educations:', error);
      setError('Failed to fetch educations');
    } finally {
      setLoading(false);
    }
  };

  // Add a new education
  const addEducation = async (educationData, images) => {
    try {
      setLoading(true);

      // Insert education data
      const { data: education, error: educationError } = await supabase
        .from('education')
        .insert([{ ...educationData, user_id: session.user.id }])
        .select()
        .single();

      if (educationError) throw educationError;

      // Upload images and insert into image table
      if (images && images.length > 0) {
        await Promise.all(
          images.map(async (image) => {
            // Pastikan ini adalah File baru (bukan URL string)
            if (!(image instanceof File)) {
              return; // Lewati jika bukan File
            }
      
            const fileExt = image.name.split('.').pop();
            const fileName = `${Math.random()}.${fileExt}`;
            const filePath = `${fileName}`;
      
            // Upload image to storage
            const { error: uploadError } = await supabase.storage
              .from('document')
              .upload(filePath, image);
      
            if (uploadError) throw uploadError;
      
            // Insert image metadata into image table
            await supabase.from('image').insert({
              user_id: session.user.id,
              category: 'education',
              category_id: education.id,
              path: filePath,
            });
          })
        );
      }

      // Refresh educations list
      await fetchEducations();
    } catch (error) {
      console.error('Error adding education:', error);
      setError('Failed to add education');
    } finally {
      setLoading(false);
    }
  };

  // Update an existing education
  const updateEducation = async (id, educationData, images) => {
    try {
      setLoading(true);

      // Update education data
      const { error } = await supabase
        .from('education')
        .update({ ...educationData })
        .eq('id', id)
        .single();

      if (error) throw error;

      // Upload new images and insert into image table
      if (images && images.length > 0) {
        await Promise.all(
          images.map(async (image) => {
            const fileExt = image.name.split('.').pop();
            const fileName = `${Math.random()}.${fileExt}`;
            const filePath = `${fileName}`;

            // Upload image to storage
            const { error: uploadError } = await supabase.storage
              .from('document')
              .upload(filePath, image);

            if (uploadError) throw uploadError;

            // Insert image metadata into image table
            await supabase.from('image').insert({
              user_id: session.user.id,
              category: 'education',
              category_id: id,
              path: filePath,
            });
          })
        );
      }

      // Refresh educations list
      await fetchEducations();
    } catch (error) {
      console.error('Error updating education:', error);
      setError('Failed to update education');
    } finally {
      setLoading(false);
    }
  };

  // Delete an education and its associated images
  const deleteEducation = async (id) => {
    try {
      setLoading(true);

      // Fetch associated images
      const { data: images, error: imagesError } = await supabase
        .from('image')
        .select('path')
        .eq('category_id', id);

      if (imagesError) throw imagesError;

      // Delete images from storage
      if (images && images.length > 0) {
        await Promise.all(
          images.map(async (image) => {
            const { error: deleteError } = await supabase.storage
              .from('document')
              .remove([image.path]);

            if (deleteError) throw deleteError;
          })
        );
      }

      // Delete image metadata from database
      await supabase.from('image').delete().eq('category_id', id);

      // Delete education data
      const { error } = await supabase
        .from('education')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Refresh educations list
      await fetchEducations();
    } catch (error) {
      console.error('Error deleting education:', error);
      setError('Failed to delete education');
    } finally {
      setLoading(false);
    }
  };

  // Delete an image
  const deleteImage = async (imagePath) => {
    try {
      // Hapus gambar dari storage
      const { error: deleteError } = await supabase.storage
        .from('document')
        .remove([imagePath]);

      if (deleteError) throw deleteError;

      // Hapus gambar dari database
      await supabase.from('image').delete().eq('path', imagePath);

      // Refresh educations list
      await fetchEducations();
    } catch (error) {
      console.error('Error deleting image:', error);
      throw error;
    }
  };

  // Fetch educations on component mount
  useEffect(() => {
    if (session) {
      fetchEducations();
    }
  }, [session]);

  return {
    educations,
    loading,
    error,
    addEducation,
    updateEducation,
    deleteEducation,
    deleteImage, // Ekspos fungsi deleteImage
  };
}